import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  shiftAt,
  siteCapacity,
  splitIntoCards,
  zoneCapacity,
  type CapacityOverrides,
  PARKING_SITES,
  SERVICE_GROUP_LABEL,
} from '@smile/shared';
import type {
  ChartBucket,
  DashboardOutlook,
  DashboardStats,
  OutlookDay,
  ReservationRow,
  SiteOccupancy,
  ZoneCars,
} from '@smile/shared';
import { api } from '../api/client';
import {
  CZK,
  LOCATION_LABEL,
  PAYMENT_LABEL,
  placeIcons,
  spotLabel,
  STATUS_CLASS,
  STATUS_LABEL,
  statusIcon,
} from '../lib/format';
import { dayDt, hoursUntil, splitDt, todayISO } from '../lib/filters';
import { can } from '../lib/perms';
import { ServiceDoneButton } from '../components/ServiceDoneButton';
import { CloseShiftForm } from '../components/CloseShiftForm';
import { Drawer, nic, rowToggle, type RowProps } from '../components/RowDrawer';
import { ServiceIcon } from '../components/ServiceIcon';
import { Footer } from '../components/Footer';

const money = (v: number) => `${CZK.format(v)} Kč`;
/** No saved capacities: the shipped file applies. One object, so useMemo keys stay stable. */
const NO_CAPS: CapacityOverrides = {};

/**
 * The two chip keys on the K převozu strip that are not a `ParkingSite`.
 *
 * Both begin with a character no site name can, so neither can ever collide
 * with 'pop' or 'sestka' however the enum grows. `NO_YARD` is the chip over
 * the cars whose spot the classifier does not recognise — it is drawn only
 * when the server sends one, because a permanent "Neurčeno 0" would train
 * people to look past the one chip that admits the screen does not know
 * something.
 */
const ALL_YARDS = '*';
const NO_YARD = '?';



/**
 * The dispatcher dashboard.
 *
 * Structure, order and wording follow the approved mockup, which in turn
 * follows the screen the staff already use: messages and till on top, the
 * shift chart, then three pairs of lists — Příjezdy | Odjezdy, Zaparkovalo |
 * Odjelo, Propadlé a zrušené | K převozu — and finally Služby across the full
 * width, which is the legacy dashboard's own last tab. Nothing has been moved for the sake
 * of being modern. Somebody who has worked here for six years should be able
 * to find every number in the place they expect it.
 *
 * Two deliberate departures from the mockup, both because Phase 0 is
 * read-only: every write-shaped control ("Uzavřít směnu", "+ nová zpráva",
 * the inline quick-action panel) is either absent or visibly labelled as a
 * later phase, and the dead space that leaves on the messages card is filled
 * with the day-level figures the API already returns. A button that does
 * nothing is worse than an honest "not yet" — the first time an operator
 * presses it and no shift closes, they stop trusting the rest of the screen.
 */


/** Stav + ID, as the legacy grid renders it. */
/** Days of stay, in the desk's own words: 1 den, 2–4 dny, 5+ dní. */
function dny(d: number | null | undefined): string {
  if (d == null || !Number.isFinite(d)) return '—';
  const n = Math.max(0, Math.round(d));
  return n === 1 ? '1 den' : n >= 2 && n <= 4 ? `${n} dny` : `${n} dní`;
}

/**
 * A transfer a driver has taken (the manager's re-test, 25 October 2026):
 * the legacy dashboard (dashboard-beta) paints the row yellow, puts ▶
 * before the button and says "Převáží <driver> od <transfer time>" on the
 * Europort card (`transfer_e2t_user_id`) and on Z Tuchoměřic / Z POPu
 * (`transfer_t2e_user_id`) — a driver id is the whole condition. The
 * reverse audit of 26 October checked it against the live old dashboard: 3
 * Europort and 6 transfer rows, the same bookings on both screens. (A
 * stricter "no end stamp" rule tried that day showed none: the stamp is
 * set while the car is still on the card.) The name is on the row itself so
 * nobody has to hover.
 */
function inProgress(p: RowProps, driver: string | null | undefined, since: string | null | undefined): RowProps {
  if (!driver) return p;
  const at = since ? since.slice(11, 16) : '';
  return { ...p, className: `${p.className} inprogress`, title: `Převáží ${driver}${at ? ` od ${at}` : ''}` } as RowProps;
}

function Prevazi({ driver, since }: { driver: string | null | undefined; since: string | null | undefined }) {
  if (!driver) return null;
  const at = since ? since.slice(11, 16) : '';
  return (
    <span className="prevazi">
      ▶ {driver}
      {at && ` od ${at}`}
    </span>
  );
}

function Stav({ r }: { r: ReservationRow }) {
  return (
    <span
      className={STATUS_CLASS[r.status] ?? 'stav gray'}
      title={`${STATUS_LABEL[r.status] ?? r.status}${r.partnerName ? ` · ${r.partnerName}` : ''}`}
    >
      <span className="ic" aria-hidden="true">
        {statusIcon(r)}
      </span>
      {r.id}
      {/* The caret the legacy chip carries, and the one class the approved
          mockup defined and nothing had yet rendered — it was drawn for
          exactly this panel. It turns when the row is open, in CSS, so the
          row callbacks do not each have to be told whether they are. */}
      <span className="caret2" aria-hidden="true">
        ▾
      </span>
    </span>
  );
}

function Klient({ r }: { r: ReservationRow }) {
  return (
    <td className="klient">
      <b className="name">{r.driverName || '—'}</b>
      {r.clubTier === 'gold' && <span className="flag gold">Gold</span>}
      {r.clubTier === 'platinum' && <span className="flag plat">VIP</span>}
      {r.called && (
        <span className="called" title="Klient telefonicky ověřen" aria-label="Klient telefonicky ověřen">
          ☎
        </span>
      )}
      {/* The old row's service icons beside the name (valet ✈ and the
          rest), as on Rezervace — the manager, 22 October 2026: "a booking
          with a service should carry its icon, like in the old system". */}
      {r.serviceIds.length > 0 && (
        <span className="icons">
          {r.serviceIds.map((id) => (
            <ServiceIcon key={id} id={id} />
          ))}
        </span>
      )}
      {r.driverPhone && <span className="tel">{r.driverPhone}</span>}
    </td>
  );
}

function Kde({ r }: { r: ReservationRow }) {
  const spot = spotLabel(r.parking);
  const loc = r.parkingLocation ? LOCATION_LABEL[r.parkingLocation] ?? r.parkingLocation : '';
  return (
    <td className="kicons" title={[loc, spot].filter(Boolean).join(' · ')}>
      {placeIcons(r)}
    </td>
  );
}

/**
 * The KeyCafe cell, the way the legacy dashboard prints it: the box code on
 * one line and, under it, where the car is to go — "> 7. patro". The code is
 * how a valet finds the key; the target is where the car goes once they have
 * it. `which` picks the arrival or the departure code: a customer uses one
 * box to leave the key and may be given another to collect it.
 */
/**
 * Where an arrival's Doporučené místo stands (the owner, 30 October 2026): the system sets every
 * car's place two hours before it arrives, so an empty place inside those two hours is one the
 * dispatcher has to fill — a person cleared it, or the system could not write it.
 */
const PLACE_LEAD_MS = 2 * 3600_000;
export function placeState(r: ReservationRow, now = Date.now()): { kind: 'auto' | 'manual' | 'missing' | 'later'; at?: string } | null {
  if (r.parkingTarget) return { kind: r.parkingTargetAuto ? 'auto' : 'manual' };
  const waiting = ['new', 'pending', 'paid'].includes(r.status) && !r.arrivalReal && r.parkingLocation !== 'airport';
  const t = Date.parse((r.arrival ?? '').replace(' ', 'T'));
  if (!waiting || !Number.isFinite(t)) return null;
  if (t - now <= PLACE_LEAD_MS) return { kind: 'missing' };
  const d = new Date(t - PLACE_LEAD_MS);
  return { kind: 'later', at: `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}` };
}

/**
 * Příjezdy's one-line tally of Doporučené místo over the rows on screen (the owner, 30 Oct 2026:
 * "I don't see how many were done automatically"). The list is windowed, so it says "v seznamu".
 */
export function placeSummary(rows: readonly ReservationRow[], now = Date.now()): ReactNode {
  const c = { auto: 0, manual: 0, missing: 0, later: 0 };
  for (const r of rows) {
    const k = placeState(r, now)?.kind;
    if (k) c[k]++;
  }
  if (!c.auto && !c.manual && !c.missing && !c.later) return undefined;
  // The owner, 30 Oct 23:45: the tally was "barely visible" — a row of coloured counts, not grey text.
  return (
    <div className="placetally" aria-label="Doporučené místo v seznamu">
      <span className="pt-label">Doporučené místo v seznamu:</span>
      <span className="pt auto">systém {c.auto}</span>
      <span className="pt manual">dispečer {c.manual}</span>
      {c.missing > 0 && <span className="pt missing">chybí {c.missing}</span>}
      {c.later > 0 && <span className="pt later">systém vybere později {c.later}</span>}
    </div>
  );
}

function KeyCafe({ r, which }: { r: ReservationRow; which: 'arrival' | 'departure' }) {
  const code = which === 'arrival' ? r.keycafeArrival : r.keycafeDeparture;
  const target = which === 'arrival' ? r.parkingTarget : null;
  const place = which === 'arrival' ? placeState(r) : null;
  if (!code && !target && !r.keycafeService && !place) return <td className="kccell">{nic}</td>;
  return (
    <td className="kccell">
      {code ? <span className="kc">{code}</span> : nic}
      {/* The service code (dispatchers, drivers) — the new system only. */}
      {r.keycafeService && (
        <span className="kcservice" title="Servisní kód pro dispečink a řidiče">
          S {r.keycafeService}
        </span>
      )}
      {target && (
        <span className="kctarget">
          &gt; {spotLabel(target)}
          {/* Who chose the place (the owner, 30 Oct 2026: "the distinction is not clear"). */}
          {place?.kind === 'auto' && (
            <span className="kcauto" title="Místo vybral systém (2 h před příjezdem). Ruční změna má vždy přednost.">
              systém
            </span>
          )}
          {place?.kind === 'manual' && (
            <span className="kcmanual" title="Místo zadal dispečer (v novém nebo starém systému). Systém ho nikdy nemění.">
              dispečer
            </span>
          )}
        </span>
      )}
      {place?.kind === 'missing' && (
        <span className="kcmissing" title="Vůz přijede do 2 hodin a nemá doporučené místo — vyberte ho a dejte Uložit jen místo.">
          Bez místa — doplní dispečer
        </span>
      )}
      {place?.kind === 'later' && (
        <span className="kclater" title="Systém vybere místo 2 hodiny před příjezdem.">
          systém vybere v {place.at}
        </span>
      )}
    </td>
  );
}

/** Where the car stands, as text — the legacy "Místo". The icon is `Kde`. */
function Misto({ r }: { r: ReservationRow }) {
  const spot = spotLabel(r.parking);
  return <td className="misto">{spot || nic}</td>;
}

/**
 * The reservation note. Trimmed on the row — a note is one line at the desk
 * and a paragraph in the drawer — with the whole text on hover. A note is
 * what the customer told us before arriving: "accessible space required",
 * "two child seats", "late flight". Empty is the common case and stays quiet.
 */
/**
 * "Klíč" on Europort and Odjezdy (the dispatchers' call of 31 Oct 2026): is the key in a KeyCafe box
 * right now, without opening the booking. The booking's last opening decides (see keyBox).
 */
function KlicBox({ r, today }: { r: ReservationRow; today: string }) {
  const k = r.keyBox;
  if (!k) return <td className="kcbox">{nic}</td>;
  const box = k.bin ? ` ${k.bin}` : '';
  return k.inBox ? (
    <td className="kcbox">
      <span className="keyin" title={`Klíč je ve schránce${box} od ${k.at.slice(8, 10)}.${k.at.slice(5, 7)}. ${k.at.slice(11, 16)}`}>
        V boxu{box && <b>{box}</b>}
      </span>
      <div className="sub">{dayDt(k.at, today)}</div>
    </td>
  ) : (
    <td className="kcbox">
      <span className="keyout" title={`Klíč vyzvednut ze schránky${box} ${k.at.slice(8, 10)}.${k.at.slice(5, 7)}. ${k.at.slice(11, 16)} — v boxu není`}>
        Vyzvednut
      </span>
      <div className="sub">{dayDt(k.at, today)}</div>
    </td>
  );
}

function Poznamka({ r }: { r: ReservationRow }) {
  const n = (r.note ?? '').trim();
  if (!n) return <td className="pozn">{nic}</td>;
  return (
    <td className="pozn" title={n}>
      {n.length > 48 ? n.slice(0, 47) + '…' : n}
    </td>
  );
}

/**
 * The legacy "Prot." column: one click prints the handover protocol for the
 * row, in a new tab, without opening the row's drawer — the click is
 * stopped before the row sees it. `noopener` because the sheet closes
 * itself and must not be able to reach back into the board.
 */
function Prot({ r }: { r: ReservationRow }) {
  return (
    <td className="prot">
      <a
        href={`/protokol/${r.id}`}
        target="_blank"
        rel="noopener"
        title="Tisk protokolu"
        aria-label={`Tisk protokolu k rezervaci ${r.id}`}
        onClick={(e) => e.stopPropagation()}
      >
        🖨
      </a>
    </td>
  );
}

function Auto({ r }: { r: ReservationRow }) {
  return (
    <>
      {r.car ? (
        <span className="carname" title={r.car}>
          {r.car}
        </span>
      ) : (
        <span className="doplnit">Doplnit auto!</span>
      )}{' '}
      {r.spz && <span className="spz">{r.spz}</span>}
    </>
  );
}

/**
 * One card. The badge is the API's `total`, not `rows.length` — a card capped
 * at sixty rows still has to say how much work there really is, and the two
 * numbers come off one query so they cannot disagree.
 *
 * Generic in the row type, with `ReservationRow` as the floor. Every card here
 * renders a booking; Služby renders a booking plus the job ordered on it, and
 * widening the parameter is what lets it reuse this component — and therefore
 * the drawer, the skeleton, the badge and the "zbytek v Rezervacích" footer —
 * instead of growing a second copy that drifts.
 */
/**
 * The return flight under Odjezdy's time (the owner, 30 Oct 2026: show a delayed flight on the main
 * page). Quiet when the flight lands as written; the wording and colours are Přílety's.
 */
function flightMinutes(m: number): string {
  const a = Math.abs(m);
  if (a < 60) return `${a} min`;
  const h = Math.floor(a / 60);
  const r = a % 60;
  return r ? `${h} h ${r} min` : `${h} h`;
}
export function DepartureFlightNote({ f }: { f: ReservationRow['departureFlight'] }) {
  if (!f || f.verdict === 'ontime') return null;
  const hm = (v: string | null) => (v ? v.slice(11, 16) : '');
  const title = `${f.number}${f.terminal ? ` · ${f.terminal}` : ''} · ${f.stateText || ''}${f.landing ? ` · přistání ${hm(f.landing)}` : ''}`;
  const atCar = f.atCar ? ` · u auta ${hm(f.atCar)}` : '';
  const d = f.flightDiffMinutes ?? 0;
  let cls = 'late';
  let text = '';
  switch (f.verdict) {
    case 'later':
      text = `✈ ${f.number} o ${flightMinutes(d)} později${atCar}`;
      break;
    case 'earlier':
      cls = 'early';
      text = `✈ ${f.number} o ${flightMinutes(d)} dřív${atCar}`;
      break;
    case 'beyond':
      cls = 'bad';
      text = `✈ ${f.number} o ${flightMinutes(f.diffMinutes ?? d)} později · přes 4 h${atCar}`;
      break;
    case 'cancelled':
      cls = 'bad';
      text = `✈ ${f.number} let zrušen`;
      break;
  }
  return (
    <div>
      <span className={`pl-v ${cls}`} title={title}>{text}</span>
    </div>
  );
}

function SectionCard<T extends ReservationRow>({
  title,
  data,
  isPending,
  cols,
  head,
  row,
  empty,
  badge,
  alert,
  note,
  rowKey,
  solo,
  overflow,
  tabs,
  activeTab,
  onTab,
  openKey,
  setOpenKey,
  expand,
}: {
  title: string;
  data?: { rows: T[]; total: number };
  /** The strip the old board showed open under a row of this card (RowDrawer `expand`). */
  expand?: 'arrive' | 'depart';
  isPending: boolean;
  cols: number;
  head: ReactNode;
  row: (r: T, open: (r: T) => RowProps) => ReactNode;
  empty: string;
  badge?: string;
  /** A warning beside the count — Příjezdy: how many cars due within 2 h have no place. */
  alert?: string;
  /**
   * A sentence under the heading, for when this card does not mean quite what
   * the rest of the screen means.
   *
   * It exists for the yard filter. Two of the six lists cannot honestly be
   * scoped to a car park — a booking that has not arrived is not standing
   * anywhere yet, and a cancellation never stood anywhere at all — and the
   * `parking` column proves it: of 2 255 pending arrivals on the staging
   * archive, 6 carry a spot; of 13 533 cancelled and no-show rows, 213 do.
   * Filtering them is therefore correct and produces a card that reads as
   * broken. This line is the difference between "no arrivals this shift" and
   * "arrivals are not filed under a car park", and the operator needs the
   * second one.
   */
  note?: ReactNode;
  /**
   * A strip of chips under the heading, each naming a slice of this card and
   * carrying its own count. Only K převozu has one, and it has one because
   * the legacy dashboard prints exactly this strip — "Z Tuchoměřic 68,
   * Z POPu 91, Ze Šestky 0, Z Kněževse 38" — and the dispatchers read it
   * before they read the list.
   *
   * The card does not filter anything itself: the parent decides what `data`
   * holds for the chip that is lit. That keeps the rows and the badge coming
   * from one place — the server — instead of the card re-deriving one of them.
   */
  tabs?: readonly { key: string; label: string; total: number }[];
  activeTab?: string;
  onTab?: (key: string) => void;
  /**
   * Distinguishes two rows carrying the same reservation id. Only Služby needs
   * it: a car booked for a wash *and* an interior clean is two rows, and
   * without this both drawers open on one click.
   */
  rowKey?: (r: T) => string;
  /** Renders across the full width instead of inside a `.pair`. */
  solo?: boolean;
  /**
   * What the card says when it is showing fewer rows than its badge counts.
   *
   * The default sends the reader to Rezervace, which is true of every card
   * that lists reservations: narrow the dates there and the rows the card left
   * out are all present. It is NOT true of Služby. Nothing on that grid
   * filters by "has an extra service that is still not done", so a job past
   * the cap has nowhere to be looked up, and a card that points at a screen
   * which cannot answer is worse than one that admits the cap — the operator
   * goes, searches, finds nothing, and concludes the job does not exist.
   */
  overflow?: (shown: number, total: number) => string;
  openKey: string | null;
  setOpenKey: (k: string | null) => void;
}) {
  const all = data?.rows ?? [];
  /* Rows are dealt out a page at a time (30 September: "it is too many now
     on the page"). The card opens on the first ten, and "Zobrazit dalších"
     adds a page from what the API already sent — no request, the fetch is
     still SECTION_LIMIT — until the fetched rows are out, when the footer
     says the rest is in Rezervace as before. Any row can still be opened. */
  const [shown, setShown] = useState(ROWS_PER_PAGE);
  const rows = all.slice(0, shown);
  const more = all.length - rows.length;
  const hidden = data ? data.total - all.length : 0;
  const toggle = rowToggle(openKey, setOpenKey);
  // The key is card-qualified, not just the id, because one reservation can
  // legitimately sit in two cards at once — a departure this shift is also in
  // the K převozu forecast. Keying on the id alone would open both.
  const keyFor = (r: T) => `${title}:${rowKey ? rowKey(r) : r.id}`;

  /* Folding (30 September: "the cards should be unfoldable when needed,
     right now the main dashboard is too long"). The heading stays, with its
     count, and the list under it opens on a click. Příjezdy and Odjezdy open
     by default — they are the shift's work — and the rest start folded; a
     choice is remembered per card in this browser, so the screen opens the
     way the dispatcher left it. */
  const [folded, setFolded] = useState<boolean>(() => readFold(title));
  const toggleFold = () => {
    const next = !folded;
    setFolded(next);
    writeFold(title, next);
  };

  return (
    <div className={`card section${solo ? ' solo' : ''}${folded ? ' folded' : ''}`}>
      <h2>
        <button
          type="button"
          className="foldbtn"
          onClick={toggleFold}
          aria-expanded={!folded}
          title={folded ? 'Rozbalit' : 'Sbalit'}
        >
          <span className="chev" aria-hidden="true">{folded ? '▸' : '▾'}</span>
          {title}
        </button>
        <span className="count">{badge ?? (data ? CZK.format(data.total) : '…')}</span>
        {alert && <span className="cardalert">{alert}</span>}
      </h2>
      {folded ? null : (
      <>
      {note && <div className="basis cardnote">{note}</div>}
      {tabs && tabs.length > 0 && (
        <div className="mtabs" role="group" aria-label={`${title} — odkud`}>
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              className={t.key === activeTab ? 'active' : ''}
              aria-pressed={t.key === activeTab}
              onClick={() => onTab?.(t.key)}
            >
              {t.label}{' '}
              <span className={`badge${t.total === 0 ? ' zero' : ''}`}>{CZK.format(t.total)}</span>
            </button>
          ))}
        </div>
      )}
      <table>
        <thead>{head}</thead>
        <tbody>
          {isPending &&
            Array.from({ length: 4 }, (_, i) => (
              <tr key={`sk${i}`}>
                {Array.from({ length: cols }, (_, j) => (
                  <td key={j}>
                    <div className="skeleton" />
                  </td>
                ))}
              </tr>
            ))}

          {!isPending && rows.length === 0 && (
            <tr>
              <td colSpan={cols} className="state">
                {empty}
              </td>
            </tr>
          )}

          {rows.map((r) => {
            const k = keyFor(r);
            return (
              <Fragment key={k}>
                {row(r, (rr) => toggle(rr, k))}
                {openKey === k && (
                  <tr className="drawerrow">
                    <td colSpan={cols}>
                      <Drawer r={r} onClose={() => setOpenKey(null)} expand={expand} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      {(more > 0 || shown > ROWS_PER_PAGE) && (
        <div className="basis morerow">
          {more > 0 && (
            <button type="button" className="btn sm" onClick={() => setShown((n) => n + ROWS_PER_PAGE)}>
              Zobrazit dalších {CZK.format(Math.min(ROWS_PER_PAGE, more))}
            </button>
          )}
          {/* The way back: "you did unfold, but not folding back". */}
          {shown > ROWS_PER_PAGE && (
            <button type="button" className="btn sm" onClick={() => setShown(ROWS_PER_PAGE)}>
              Sbalit na {ROWS_PER_PAGE}
            </button>
          )}
          <span>
            zobrazeno {rows.length} z {CZK.format(data!.total)}
            {more === 0 && hidden > 0
              ? overflow
                ? ` — ${overflow(rows.length, data!.total)}`
                : ' — zbytek v Rezervacích.'
              : ''}
          </span>
        </div>
      )}
      {shown <= ROWS_PER_PAGE && more === 0 && hidden > 0 && (
        <div className="basis">
          {overflow
            ? overflow(rows.length, data!.total)
            : `Zobrazeno prvních ${rows.length} z ${CZK.format(data!.total)} — zbytek v Rezervacích.`}
        </div>
      )}
      </>
      )}
    </div>
  );
}

/** How many rows a card shows at a time; "Zobrazit dalších" adds this many. */
const ROWS_PER_PAGE = 10;

/** The cards that open by default; everything else starts folded. */
const OPEN_BY_DEFAULT = new Set(['Příjezdy', 'Odjezdy']);

function readFold(title: string): boolean {
  try {
    const v = localStorage.getItem(`smile-fold:${title}`);
    if (v === '1') return true;
    if (v === '0') return false;
  } catch {
    /* private mode — the default applies */
  }
  return !OPEN_BY_DEFAULT.has(title);
}

function writeFold(title: string, folded: boolean): void {
  try {
    localStorage.setItem(`smile-fold:${title}`, folded ? '1' : '0');
  } catch {
    /* private mode — the choice lasts the page */
  }
}

/* ------------------------------------------------------------------ *
 * The per-site strip — "should be visible in the dashboard"
 * ------------------------------------------------------------------ */

/**
 * How many cars are standing at each yard, on the default view.
 *
 * The numbers themselves are not new: the Obsazenost panel has printed them
 * for a while. The problem was where. Obsazenost is the second tab, the board
 * opens on Aktuální směna, and a dispatcher who never presses the tab never
 * sees the figure — which is the same, from the desk, as the figure not
 * existing. The owner's sentence was "the number of cars staying in different
 * locations should be visible in the dashboard", and "visible" is the whole
 * of the request.
 *
 * So this is one line, above the board, on both tabs: five yards (six cards —
 * POP is two) with the same counts, the same denominators and the same
 * headings the panel below uses, because they come from the same
 * `splitIntoCards` call on the same payload. Not a second request and not a
 * second arithmetic: a strip that could disagree with the panel one tab away
 * would be worse than no strip.
 *
 * Each figure is a button rather than text, and pressing it opens the panel
 * at that card — the drill-down an operator will want the moment a number
 * looks wrong is one click away rather than a tab and a scroll.
 *
 * "Neurčeno" rides along only when it is holding something. It is the board
 * admitting it cannot place a car, and hiding that on the default view is
 * exactly the mistake this strip exists to undo.
 */
function SiteStrip({
  data,
  caps,
  isPending,
  onPick,
}: {
  data: SiteOccupancy[];
  caps: CapacityOverrides;
  isPending: boolean;
  onPick: (key: string) => void;
}) {
  const cards = useMemo(() => splitIntoCards(data, caps), [data, caps]);

  /* An empty yard is drawn; an unknown bucket holding nothing is not. The
     difference is that "Šestka 0" is a fact an operator wants — the yard is
     clear — and "Neurčeno 0" is a row that says nothing on every normal day
     and would train the eye to skip the place where the exceptions appear. */
  const shown = cards.filter((b) => b.site !== null || b.total > 0);
  const total = cards.reduce((n, b) => n + b.total, 0);

  return (
    <div className="sitestrip" aria-label="Vozy na parkovištích">
      {isPending ? (
        <span className="loading">Načítám obsazenost…</span>
      ) : (
        <>
          {shown.map((b) => (
            <button
              key={b.key}
              type="button"
              className={b.site === null ? 'yard unknown' : 'yard'}
              onClick={() => onPick(b.key)}
              title={
                b.capacity > 0
                  ? `${b.label}: ${b.total} z ${b.capacity} míst — rozpad podle míst.`
                  : `${b.label}: ${b.total} vozů — rozpad podle míst.`
              }
            >
              <span className="lbl">{b.label}</span>
              <span className="n">
                {CZK.format(b.total)}
                {b.capacity > 0 && <em> / {CZK.format(b.capacity)}</em>}
              </span>
            </button>
          ))}
          {/* The same number the card above prints as "na parkovišti", summed
              from the cards rather than read from a second field, so the strip
              cannot add up to something its own total contradicts. */}
          <span className="all">
            celkem <b>{CZK.format(total)}</b>
          </span>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Chart
 * ------------------------------------------------------------------ */

/**
 * One bar per hour of the shift, four stacked segments: arrivals and
 * departures, each split into "all of them" and "the part that still needs a
 * car physically moved". Hours already behind the clock show a full bar with
 * no pending segment, which is correct — that work is done.
 *
 * The scale is computed from the data and rounded up to a multiple of three so
 * the three gridlines land on whole numbers. A fixed axis would flatten a
 * quiet night into a row of stubs and clip a busy Friday.
 */
function Chart({ buckets }: { buckets: ChartBucket[] }) {
  const totals = buckets.map((b) => b.arrivals + b.departures);
  const step = Math.max(1, Math.ceil(Math.max(1, ...totals) / 3));
  const top = step * 3;
  const px = (v: number) => `${(v / top) * 150}px`;

  return (
    <div className="card chartcard">
      <h4>Plánované Příjezdy a Odjezdy na směnu</h4>
      <div className="bars">
        {[1, 2, 3].map((n) => (
          <div key={n} className="gridline" style={{ bottom: `${n * 33}%` }}>
            <span>{step * n}</span>
          </div>
        ))}
        {buckets.map((b) => {
          const segs: Array<[number, string]> = [
            [b.arrivals - b.arrivalsValet, 'var(--chart-arr)'],
            [b.arrivalsValet, 'var(--chart-arr2)'],
            [b.departures - b.departuresPending, 'var(--chart-dep)'],
            [b.departuresPending, 'var(--chart-dep2)'],
          ];
          return (
            <div
              key={b.hour}
              className="col"
              data-tip={`${b.hour}:00 — příjezdy ${b.arrivals}, odjezdy ${b.departures}`}
            >
              <div className="stack">
                {segs.map(([v, bg], i) =>
                  v > 0 ? (
                    <div key={i} className="seg" style={{ height: px(v), background: bg }} />
                  ) : null,
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="baseline" />
      <div className="xlab">
        {buckets.map((b) => (
          <span key={b.hour}>{b.hour}:00</span>
        ))}
      </div>
      <div className="legend">
        <span>
          <i style={{ background: 'var(--chart-arr)' }} />
          Příjezdy
        </span>
        <span title="Příjezdy s objednanou službou valet — jako na stávajícím dashboardu.">
          <i style={{ background: 'var(--chart-arr2)' }} />
          Příjezdy Valet
        </span>
        <span>
          <i style={{ background: 'var(--chart-dep)' }} />
          Odjezdy
        </span>
        <span title="Vozy, které ještě nejsou přivezené zpět na Europport.">
          <i style={{ background: 'var(--chart-dep2)' }} />
          Odjezdy k převozu
        </span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Obsazenost — where the cars actually are
 * ------------------------------------------------------------------ */

/**
 * The occupancy panel, one card per site.
 *
 * The legacy screen prints "367 / 483" — a count over a capacity — and this
 * one prints it again. It did not for a while: the `capacity` table in the
 * legacy database holds nothing at all for POP, Kněževes and Šestka, and what
 * it holds for Tuchoměřice sums to figures the legacy panel does not show, so
 * for a few weeks the panel printed the count alone rather than a ratio whose
 * bottom half it had invented.
 *
 * The denominators now come from `capacity.ts` in @smile/shared, which is a
 * plain file of numbers the owner edits — seeded with the values the legacy
 * panel prints, listing every zone name the yards have actually recorded. A
 * site left at 0 there still prints the count alone, so a yard nobody has
 * counted is visibly uncounted instead of quietly wrong.
 *
 * The bars are still scaled against the fullest zone on the same card rather
 * than against a per-zone capacity, because almost every zone is still at 0.
 * A zone that HAS been counted also prints its own "12 / 20" beside the count;
 * the bar underneath it does not change meaning halfway down a list.
 *
 * A site is a card, except POP, which is two — "POP venku" and "POP garáž",
 * measured against 150 and 650, the way the legacy panel has always printed
 * it. That split lives in capacity.ts beside the numbers it splits, so the
 * owner can change which zones are inside and which are on the strip without
 * anyone touching this file. `splitIntoCards` is what does it; the total in
 * the footer is summed from the cards, so it is right either way.
 *
 * "Neurčeno" appears only when something is in it. It is the panel admitting
 * it does not know where a car is, and it should be rare enough to be worth
 * chasing when it shows up.
 */
/**
 * The occupancy outlook — the legacy Obsazenost tab's actual content.
 *
 * Sixty days, two stacked areas: Venku (Economy, parked outside) under Garáž
 * (everything else), from the cars here now and the bookings on the books.
 * Under it a thin strip of each day's planned arrivals (up, red) and
 * departures (down, blue), which the legacy overlays on the same axes and
 * which on a sixty-day chart are unreadable there. The dashed line is the
 * company's capacity from capacity.ts — the one number the legacy chart
 * does not draw and the one question this chart exists to answer.
 *
 * Plain SVG, like the shift chart: sixty points of two series is not a case
 * for a library, and a library is one more thing that can break at 22:00.
 */
function OutlookChart({ data, isPending, caps }: { data?: DashboardOutlook; isPending: boolean; caps: CapacityOverrides }) {
  const days = data?.days ?? [];
  const cap = PARKING_SITES.reduce((s, k) => s + siteCapacity(k, caps), 0);
  const W = 1000;
  const H = 240;
  const PAD = { l: 44, r: 12, t: 12, b: 26 };
  const STRIP = 44;
  const iw = W - PAD.l - PAD.r;
  const ih = H - PAD.t - PAD.b;
  const maxY = Math.max(cap, ...days.map((d) => d.total), 1);
  const x = (i: number) => PAD.l + (days.length > 1 ? (i / (days.length - 1)) * iw : 0);
  const y = (v: number) => PAD.t + ih - (v / maxY) * ih;
  const area = (top: (d: OutlookDay) => number, bottom: (d: OutlookDay) => number) => {
    if (!days.length) return '';
    const up = days.map((d, i) => `${x(i).toFixed(1)},${y(top(d)).toFixed(1)}`);
    const down = days.map((d, i) => `${x(i).toFixed(1)},${y(bottom(d)).toFixed(1)}`).reverse();
    return `M${up.join('L')}L${down.join('L')}Z`;
  };
  const maxMove = Math.max(1, ...days.map((d) => Math.max(d.arrivals, d.departures)));
  // Round ticks — 500, 1 000, 1 500 — not quarters of whatever the maximum
  // happens to be. A gridline at 943 is a number nobody plans against.
  const rawStep = maxY / 4;
  const mag = 10 ** Math.floor(Math.log10(rawStep));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= rawStep) ?? mag;
  const ticks: number[] = [];
  for (let v = step; v <= maxY; v += step) ticks.push(Math.round(v));
  const label = (iso: string) => {
    const [, m, d] = iso.split('-');
    return `${Number(d)}. ${Number(m)}.`;
  };
  const every = days.length > 40 ? 6 : days.length > 20 ? 3 : 1;

  return (
    <div className="card outlook">
      <div className="h">
        <h4>Obsazenost — výhled na 60 dní</h4>
        <span className="sub">
          Vozy na parkovištích podle rezervací: ty, které tu stojí, plus plánované příjezdy minus
          plánované odjezdy. Klesá tam, kde rezervace ještě nejsou.
        </span>
      </div>
      {isPending && !data ? (
        <div className="skeleton" style={{ height: H + STRIP }} />
      ) : (
        <svg viewBox={`0 0 ${W} ${H + STRIP}`} className="outlook-svg" role="img" aria-label="Výhled obsazenosti">
          {ticks.map((v) => (
            <g key={v}>
              <line x1={PAD.l} x2={W - PAD.r} y1={y(v)} y2={y(v)} className="grid" />
              <text x={PAD.l - 6} y={y(v) + 4} className="tick" textAnchor="end">
                {CZK.format(v)}
              </text>
            </g>
          ))}
          <path d={area((d) => d.venku, () => 0)} className="venku" />
          <path d={area((d) => d.total, (d) => d.venku)} className="garaz" />
          {cap > 0 && (
            <g>
              <line x1={PAD.l} x2={W - PAD.r} y1={y(cap)} y2={y(cap)} className="cap" />
              <text x={W - PAD.r} y={y(cap) - 5} className="caplabel" textAnchor="end">
                kapacita {CZK.format(cap)}
              </text>
            </g>
          )}
          {days.map((d, i) =>
            i % every === 0 ? (
              <text key={d.date} x={x(i)} y={H - 8} className="tick" textAnchor="middle">
                {label(d.date)}
              </text>
            ) : null,
          )}
          {/* The movement strip: arrivals up from a baseline, departures down. */}
          {(() => {
            const base = H + STRIP / 2;
            const half = STRIP / 2 - 4;
            const bw = Math.max(1.5, (iw / Math.max(1, days.length)) * 0.6);
            return days.map((d, i) => (
              <g key={d.date}>
                <title>{`${label(d.date)}: ${CZK.format(d.total)} vozů (venku ${CZK.format(d.venku)}, garáž ${CZK.format(d.garaz)}) · příjezdy ${d.arrivals} · odjezdy ${d.departures}`}</title>
                <rect
                  x={x(i) - bw / 2}
                  y={base - (d.arrivals / maxMove) * half}
                  width={bw}
                  height={(d.arrivals / maxMove) * half}
                  className="arr"
                />
                <rect x={x(i) - bw / 2} y={base} width={bw} height={(d.departures / maxMove) * half} className="dep" />
                <rect x={x(i) - iw / Math.max(1, days.length) / 2} y={PAD.t} width={iw / Math.max(1, days.length)} height={H + STRIP - PAD.t} className="hit" />
              </g>
            ));
          })()}
          <line x1={PAD.l} x2={W - PAD.r} y1={H + STRIP / 2} y2={H + STRIP / 2} className="grid" />
        </svg>
      )}
      <div className="legend">
        <span>
          <i className="sw venku" /> Venku
        </span>
        <span>
          <i className="sw garaz" /> Garáž
        </span>
        <span>
          <i className="sw arr" /> Příjezdy
        </span>
        <span>
          <i className="sw dep" /> Odjezdy
        </span>
        <span>
          <i className="sw cap" /> Kapacita
        </span>
      </div>
    </div>
  );
}

function Occupancy({
  data,
  caps,
  isPending,
  openKey,
  setOpenKey,
  focusCard,
  clearFocus,
}: {
  data: SiteOccupancy[];
  caps: CapacityOverrides;
  isPending: boolean;
  openKey: string | null;
  setOpenKey: (k: string | null) => void;
  /** The card the strip above was pressed on, or null. */
  focusCard: string | null;
  clearFocus: () => void;
}) {
  const cards = useMemo(() => splitIntoCards(data, caps), [data, caps]);

  /* The outlook is fetched when this tab is open and nowhere else: sixty days
     of bookings is a different question from where cars are standing, and it
     changes with bookings, not with the shift. Five minutes is plenty. */
  const outlookQ = useQuery({
    queryKey: ['dashboard', 'outlook'],
    queryFn: () => api.outlook(),
    staleTime: 5 * 60_000,
    refetchInterval: 5 * 60_000,
  });
  const total = cards.reduce((n, b) => n + b.total, 0);

  /* Bring the pressed card into view once, then forget it. Forgetting is the
     part that matters: the panel re-renders every fifteen seconds on the
     clock tick, and a focus that survived would drag the page back under an
     operator who had scrolled somewhere else. The card is found by id rather
     than held in a ref because it does not exist until this component mounts,
     which is what the tab switch does. */
  useEffect(() => {
    if (focusCard === null) return;
    const el = document.getElementById(`site-${focusCard}`);
    /* No element is a card that was on the strip and is not in the panel —
       impossible while both read the same splitIntoCards, and harmless: the
       tab has already switched, so the numbers are on screen regardless. */
    if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    clearFocus();
  }, [focusCard, clearFocus]);

  /* Which zone the operator asked to see inside. Held here rather than up in
     Dashboard because switching the strip back to Aktuální směna unmounts
     this component, and closing the list when you leave the car park is the
     behaviour you would have had to write by hand otherwise. */
  const [zone, setZone] = useState<{ zone: string; label: string; site: string } | null>(null);

  /* Gated on the click, so nothing is asked for until somebody asks. Sixty
     seconds matches the dashboard behind it: a list of cars that disagrees
     with the count above it, because one of the two is a minute old, is the
     kind of thing that gets a screen distrusted. */
  const carsQ = useQuery({
    queryKey: ['dashboard', 'zone', zone?.zone ?? ''],
    queryFn: () => api.occupancyZone({ zone: zone!.zone }),
    enabled: zone !== null,
    refetchInterval: 60_000,
  });

  /* The answer carries the zone it is an answer to. Painting a slow reply for
     "Tráva E" under a heading that now says "Hala 1" is worse than painting
     nothing: both are lists of real cars, and there is no way to tell from
     the screen which one you are looking at. */
  const cars = carsQ.data && zone && carsQ.data.zone === zone.zone ? carsQ.data : undefined;

  /* Escape closes one layer at a time: the reservation panel first, then the
     list it was opened from. Both listeners are on `window`, and this one is
     registered first — child effects run before the parent's — so the guard
     on `openKey` is what makes the order hold rather than luck. Collapsing
     both at once would take away the list an operator was halfway down
     because they closed a panel they had opened by accident. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && openKey === null) setZone(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openKey]);

  const openZone = (z: string, label: string, site: string) => {
    /* A reservation panel opened from the previous zone must not survive into
       the next one — it would be a car that is not in the list under it. */
    setOpenKey(null);
    setZone((cur) => (cur?.zone === z ? null : { zone: z, label, site }));
  };

  if (isPending) {
    return <div className="obsazenost loading">Načítám obsazenost…</div>;
  }

  return (
    <>
      {/* The legacy Obsazenost tab is this chart; the zone cards under it
          are what the legacy prints at the top of every view instead. */}
      <OutlookChart data={outlookQ.data} isPending={outlookQ.isPending} caps={caps} />
      <div className="obsazenost">
        {cards.map((b) => {
          // Scale the bars against the fullest zone HERE, not across cards:
          // Tuchoměřice's two hundred on the grass would otherwise flatten
          // every bar on Šestka into an indistinguishable sliver.
          const top = Math.max(1, ...b.zones.map((z) => z.count));
          // 0 when nobody has counted this card yet — and when the bucket is
          // the null one, which is not a place and cannot have a capacity.
          const cap = b.capacity;
          return (
            <div
              className={b.site === null ? 'site unknown' : 'site'}
              key={b.key}
              id={`site-${b.key}`}
            >
              <div className="h">
                <h3>{b.label}</h3>
                <span className="n">
                  {CZK.format(b.total)}
                  {cap > 0 && <em> / {CZK.format(cap)}</em>}
                </span>
              </div>
              {b.zones.length === 0 ? (
                <div className="empty">Prázdno</div>
              ) : (
                <ul>
                  {b.zones.map((z) => {
                    /* Every row opens, including the zero ones and including
                       the blank bucket. A row that looks the same as its
                       neighbour but does nothing when clicked is how an
                       operator concludes the feature is broken. */
                    const isOpen = zone?.zone === z.zone;
                    const label = spotLabel(z.zone);
                    const pick = () => openZone(z.zone, label, b.label);
                    return (
                      <li
                        key={z.zone}
                        className={isOpen ? 'open' : undefined}
                        tabIndex={0}
                        role="button"
                        aria-expanded={isOpen}
                        aria-label={`${b.label}, ${label}: ${z.count} vozů — zobrazit seznam`}
                        onClick={() => {
                          // Same guard the reservation rows use: selecting a
                          // spot name to paste it must not toggle the list.
                          if (window.getSelection()?.toString()) return;
                          pick();
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            pick();
                          }
                        }}
                      >
                        <span className="z" title={z.zone}>
                          {label}
                        </span>
                        <span className="bar" aria-hidden="true">
                          <i style={{ width: `${Math.round((z.count / top) * 100)}%` }} />
                        </span>
                        <span className="c">
                          {CZK.format(z.count)}
                          {zoneCapacity(b.site, z.zone) > 0 && (
                            <em> / {CZK.format(zoneCapacity(b.site, z.zone))}</em>
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
      </div>
      {zone && (
        <ZoneCarList
          site={zone.site}
          label={zone.label}
          data={cars}
          isPending={carsQ.isPending || cars === undefined}
          isError={carsQ.isError}
          error={carsQ.error}
          onClose={() => {
            setZone(null);
            setOpenKey(null);
          }}
          openKey={openKey}
          setOpenKey={setOpenKey}
        />
      )}
      <div className="obsazenost-foot">
        Celkem <b>{CZK.format(total)}</b> vozů na parkovištích — vozy se zaznamenaným příjezdem a
        bez odjezdu. Kapacity míst jsou převzaté ze starého systému a jsou k úpravě v souboru{' '}
        <code>packages/shared/src/capacity.ts</code>, kde je u každého parkoviště seznam
        skutečných stání. Editace přímo v aplikaci <span className="phase">FÁZE 1</span>.
      </div>
    </>
  );
}

/**
 * The cars standing in one zone, under the card grid.
 *
 * This is the answer to "what car stays where", and it is deliberately not a
 * screen of its own. A dispatcher checking Tráva E is usually doing it while
 * something else is happening; sending them to a page called Obsazenost costs
 * them the arrivals list they were reading, and the way back is finding their
 * place in it again. So the list unfolds here, full width under the cards,
 * and every row opens the same reservation panel the six lists below use —
 * one pattern learned once, three places it works.
 *
 * The band is under the whole grid rather than inside the card, because a
 * card is a fifth of the width and a reservation row is not. Widening the
 * card would push the other four off the row every time somebody clicked.
 */
function ZoneCarList({
  site,
  label,
  data,
  isPending,
  isError,
  error,
  onClose,
  openKey,
  setOpenKey,
}: {
  site: string;
  label: string;
  data?: ZoneCars;
  isPending: boolean;
  isError: boolean;
  error: unknown;
  onClose: () => void;
  openKey: string | null;
  setOpenKey: (k: string | null) => void;
}) {
  const today = todayISO();
  const rows = data?.rows ?? [];
  const hidden = data ? data.total - rows.length : 0;
  const toggle = rowToggle(openKey, setOpenKey);
  const COLS = 6;

  return (
    /* `card section` before `zonecars`: the band is the same object as the six
       lists below it — a card with a table of reservations in it — and saying
       so in the class list is what makes it stay that way. Copying forty lines
       of table styling under a new name is how the row that highlights on
       hover here stops matching the row that highlights on hover there. */
    <div className="card section zonecars">
      <div className="zh">
        <h3>
          {site} · <b>{label}</b>
        </h3>
        <span className="n">{data ? `${CZK.format(data.total)} vozů` : '…'}</span>
        <button type="button" className="dclose" onClick={onClose} aria-label="Zavřít seznam vozů">
          ✕
        </button>
      </div>

      <table>
        <thead>
          <tr>
            <th>Stav+ID</th>
            <th>Klient</th>
            <th>Auto</th>
            <th>SPZ</th>
            <th>Přijel</th>
            <th>Odjezd — plán</th>
          </tr>
        </thead>
        <tbody>
          {isError && (
            <tr>
              <td colSpan={COLS} className="state err">
                {error instanceof Error ? error.message : 'Seznam vozů se nepodařilo načíst.'}
              </td>
            </tr>
          )}

          {!isError &&
            isPending &&
            Array.from({ length: 3 }, (_, i) => (
              <tr key={`zsk${i}`}>
                {Array.from({ length: COLS }, (_, j) => (
                  <td key={j}>
                    <div className="skeleton" />
                  </td>
                ))}
              </tr>
            ))}

          {!isError && !isPending && rows.length === 0 && (
            <tr>
              <td colSpan={COLS} className="state">
                Na tomto stání teď nestojí žádný vůz.
              </td>
            </tr>
          )}

          {!isError &&
            !isPending &&
            rows.map((r) => {
              /* Card-qualified, like the six lists: the same reservation can
                 be open in Odjezdy and here at once, and keying on the id
                 alone would unfold both. */
              const k = `Obsazenost:${r.id}`;
              return (
                <Fragment key={k}>
                  <tr {...toggle(r, k)}>
                    <td>
                      <Stav r={r} />
                    </td>
                    <Klient r={r} />
                    <td>
                      {r.car ? (
                        <span className="carname" title={r.car}>
                          {r.car}
                        </span>
                      ) : (
                        <span className="doplnit">Doplnit auto!</span>
                      )}
                    </td>
                    <td>
                      {r.spz ? (
                        <span className="spz">{r.spz}</span>
                      ) : (
                        <span className="doplnit">Doplnit SPZ!</span>
                      )}
                    </td>
                    {/* The real arrival, not the booked one: this list is the
                        cars that are physically here, and that is the stamp
                        the server selected them by. */}
                    <td className="time">{dayDt(r.arrivalReal, today)}</td>
                    <td className="time">{dayDt(r.departure, today)}</td>
                  </tr>
                  {openKey === k && (
                    <tr className="drawerrow">
                      <td colSpan={COLS}>
                        <Drawer r={r} onClose={() => setOpenKey(null)} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
        </tbody>
      </table>

      <div className="basis">
        Řazeno podle plánovaného odjezdu — první odjíždí, první se veze.
        {hidden > 0 && (
          <>
            {' '}
            Zobrazeno prvních {rows.length} z {CZK.format(data!.total)}.
          </>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Screen
 * ------------------------------------------------------------------ */

export function Dashboard() {
  const navigate = useNavigate();
  const [term, setTerm] = useState('');
  const [clock, setClock] = useState(() => new Date());
  /* Which view the strip is on. It swaps only the band under the strip — the
     chart or the car park — and leaves the six lists below it alone. The
     legacy Obsazenost is a whole separate page and going to it means losing
     sight of the arrivals; here nothing an operator was already looking at
     disappears when they check where a car is standing. */
  const [tab, setTab] = useState<'smena' | 'obsazenost' | 'sluzby'>('smena');
  /* Which card the strip was pressed on, so the panel can bring it into view.
     Held as a key rather than a ref because the panel is not mounted at the
     moment of the click — the tab switch is what mounts it. */
  const [focusCard, setFocusCard] = useState<string | null>(null);
  /* Which row is unfolded, as "<card>:<id>". One at a time across the whole
     screen: two panels open at once push the lists apart and the operator
     loses the very thing the panel exists to preserve. */
  const [openKey, setOpenKey] = useState<string | null>(null);
  /* Which yard the K převozu card is showing. '*' is Vše, the whole queue,
     and it is what the card opens on — the same default the legacy strip has
     and the only one that answers "how much is there to do" in one number. */
  const [transferYard, setTransferYard] = useState<string>(ALL_YARDS);
  /* Which yard the six lists are scoped to. '*' is no filter and is what the
     board opens on — a dashboard that remembered yesterday's chip would show
     an operator a partial shift they never asked for and cannot see the
     reason for. */
  /* 4 October 2026: the board's own place filter (the Místo chips) is gone —
     the desk works from /dashboard-beta, where the place matters only on the
     transfer tabs, and a filter that zeroed Příjezdy and Odjezdy by design
     muddled more than it showed. The API still accepts one; nothing sends it. */
  const siteFilter: string | undefined = undefined;

  // The header clock is the operator's own, not the server's: it is there so
  // they can see the screen is alive, and a frozen clock next to live figures
  // reads as a frozen application.
  useEffect(() => {
    const id = setInterval(() => setClock(new Date()), 15_000);
    return () => clearInterval(id);
  }, []);

  // Escape closes the panel. It is the key people already press to get out of
  // things, and without it the only way back is finding the row again.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenKey(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /* Which shift the board describes. 0 is now; 1..6 are the next three days,
     shift by shift — the legacy "Další směny" drop-down. It is screen state
     rather than URL state on purpose: the dashboard is the screen left open
     all shift, and a forecast that survived a reload would be a board that
     looks live and is not. Reload, and it is now again. */
  const [shiftOffset, setShiftOffset] = useState(0);

  const { data, isPending, isError, error, isFetching, refetch } = useQuery({
    /* The chip is in the key, so switching yards is a fetch of its own and
       the two answers are cached apart. Going back to "Všechna místa" then
       paints the numbers that were already there instead of blanking the
       board for a round trip. The shift is in it for the same reason. */
    queryKey: ['dashboard', siteFilter ?? '*', shiftOffset],
    queryFn: () => api.dashboard(siteFilter, shiftOffset),
    /* Without this, pressing a chip empties the whole board. The key changes,
       react-query has nothing cached for the new key, and `isPending` goes
       true — so the chart, the occupancy panel and the till fall to skeletons
       even though not one of those three is scoped by the chip and not one of
       their numbers is going to change. Keeping the previous answer on screen
       means only the six cards move, `isFetching` drives the "aktualizuje se"
       hint, and the dispatcher keeps reading the panel while the cards catch
       up. It matters more the slower the round trip is. */
    placeholderData: keepPreviousData,
    // The dashboard is the screen left open all shift, so it refreshes on its
    // own. Sixty seconds is the legacy cadence; anything tighter is a poll
    // storm against a 2013-era MySQL box for numbers that move in minutes.
    refetchInterval: 60_000,
  });

  const s: DashboardStats | undefined = data;

  /* The legacy board's silent arrival (dashboard-beta marked every complete
     self-drive booking arrived on each page load; the owner, 18 October
     2026: the staff rely on it). The read says how many are due; this asks
     the write to do it, then refetches so the cars move to Zaparkovalo.
     Only when this person may write bookings and the action is switched on
     here — a quiet board, a forecast, or a read-only deployment never sends
     it. One attempt per answer: a refusal (another desk got there first, the
     action still in the old system) is not retried until the next minute's
     board says there is still something due. */
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.me(), staleTime: 10 * 60_000 });
  const health = useQuery({ queryKey: ['health'], queryFn: () => api.health(), staleTime: 5 * 60_000, retry: false });
  const autoVerifyOn =
    can(me.data, '/reservations/*', 'write') &&
    (health.data?.writeActions === 'all' || (Array.isArray(health.data?.writeActions) && health.data.writeActions.includes('autoverify')));
  const autoVerifyTried = useRef<DashboardStats | null>(null);
  useEffect(() => {
    if (!s || s.shiftOffset > 0 || s.autoVerifyDue <= 0 || !autoVerifyOn) return;
    if (autoVerifyTried.current === s) return;
    autoVerifyTried.current = s;
    let gone = false;
    api
      .autoVerify()
      .then((r) => {
        if (!gone && r.ids.length > 0) void refetch();
      })
      .catch(() => {
        /* Said on the next board if it persists; nothing to show for a
           background step the old page never showed either. */
      });
    return () => {
      gone = true;
    };
  }, [s, autoVerifyOn, refetch]);

  /* Which yard the numbers on screen were actually computed for, as opposed to
     which chip is pressed. They differ for one round trip after a press, and
     while they differ it is this one that the cards' notes and empty states
     have to follow — otherwise the board says "filtr místa" over rows that are
     still the unfiltered ones. The API echoes the filter it applied precisely
     so the screen never has to guess. */
  const today = useMemo(() => todayISO(0, clock), [clock]);

  /* The Služby worklist comes sorted by group (shared SERVICE_GROUP_LABEL's keys: leaving
     soon, back at Europort, the rest on the yards, not yet arrived — the
     owner's reading order of 4 October 2026). A heading is drawn over the
     first row of each group; this is the set of those rows and the count
     under each heading, so the row renderer below needs no memory. */
  const serviceGroupHeads = useMemo(() => {
    const first = new Map<string, { label: string; count: number }>();
    for (const r of s?.services.rows ?? []) {
      const k = `${r.id}:${r.serviceRef}`;
      const seen = [...first.values()].find((v) => v.label === SERVICE_GROUP_LABEL[r.group]);
      if (seen) seen.count += 1;
      else first.set(k, { label: SERVICE_GROUP_LABEL[r.group], count: 1 });
    }
    return first;
  }, [s]);

  /* Whether what is on screen is a forecast. Read from the answer, not from
     the control: for one round trip after the drop-down changes the board is
     still the old shift, and it is the board's own shift that the banner and
     the hidden cards have to follow. */
  const forecast = (s?.shiftOffset ?? 0) > 0;

  /* The drop-down's entries: the next six shifts, labelled the way the desk
     says them — "zítra denní", "pozítří noční" — with the rota hours. Built
     from the same `shiftAt` the API uses, so the label and the window cannot
     disagree. `clock` is in the deps so the list rolls over at 08:00 and
     20:00 without a reload. */
  const shiftOptions = useMemo(() => {
    const now = new Date(clock);
    const dayOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const t0 = dayOf(now);
    const DAY_MS = 86400_000;
    return [1, 2, 3, 4, 5, 6].map((n) => {
      const w = shiftAt(now, n);
      const diff = Math.round((dayOf(w.from) - t0) / DAY_MS);
      const day =
        diff === 0
          ? 'dnes'
          : diff === 1
            ? 'zítra'
            : diff === 2
              ? 'pozítří'
              : `${['ne', 'po', 'út', 'st', 'čt', 'pá', 'so'][w.from.getDay()]} ${w.from.getDate()}. ${w.from.getMonth() + 1}.`;
      const kind = w.kind === 'day' ? 'denní' : 'noční';
      const hh = (d: Date) => `${String(d.getHours()).padStart(2, '0')}:00`;
      return { n, label: `${day} ${kind} ${hh(w.from)}–${hh(w.to)}` };
    });
  }, [clock]);

  const shiftLabel = s
    ? `${s.shift.kind === 'day' ? 'denní' : 'noční'} směna · ${splitDt(s.shift.from).d} ` +
      `${splitDt(s.shift.from).t} – ${splitDt(s.shift.to).t}`
    : 'načítám data z databáze… chvilku to trvá';

  const hhmm = `${clock.getHours()}:${String(clock.getMinutes()).padStart(2, '0')}`;

  const till = s?.till;

  /* The K převozu strip: Vše first, then whatever yards the server sent, in
     the order it sent them. The counts are the server's own uncapped ones —
     nothing here measures an array, because the arrays are capped and a badge
     that quietly reported the cap would tell a dispatcher the backlog had
     stopped growing at the moment it started to matter. */
  const toTransfer = s?.sections.toTransfer;
  const yardTabs = useMemo(
    () =>
      toTransfer
        ? [
            { key: ALL_YARDS, label: 'Vše', total: toTransfer.total },
            ...toTransfer.tabs.map((t) => ({
              key: t.site ?? NO_YARD,
              label: t.label,
              total: t.total,
            })),
          ]
        : undefined,
    [toTransfer],
  );
  /* A tab can vanish between refreshes — Neurčeno appears only when something
     failed to classify, and empties as soon as somebody fixes it. Falling back
     to Vše is the difference between the card going quietly back to the whole
     queue and it sitting there empty under a chip that is no longer drawn. */
  const yard = yardTabs?.some((t) => t.key === transferYard) ? transferYard : ALL_YARDS;
  const yardData = useMemo(() => {
    if (!toTransfer) return undefined;
    if (yard === ALL_YARDS) return toTransfer;
    const t = toTransfer.tabs.find((x) => (x.site ?? NO_YARD) === yard);
    return t ? { rows: t.rows, total: t.total } : toTransfer;
  }, [toTransfer, yard]);

  return (
    <div className="container">
      <div className="pagehead">
        <h1>Dashboard</h1>
        <span className="shift">
          {isPending && <span className="spin" aria-hidden="true" />}
          {shiftLabel} · {hhmm}
          {isFetching && !isPending ? ' · aktualizuji' : ''}
          {forecast && (
            <span className="phase forecast" title="Plán podle rezervací. Směna ještě nezačala.">
              VÝHLED
            </span>
          )}
        </span>
        <div className="acts">
          <form
            role="search"
            onSubmit={(e) => {
              e.preventDefault();
              // The dashboard search is the same search as the grid's, so it
              // goes there rather than growing a second, subtly different one.
              if (term.trim()) navigate(`/rezervace?q=${encodeURIComponent(term.trim())}`);
            }}
          >
            <label className="visually-hidden" htmlFor="dq">
              Hledat rezervace
            </label>
            <input
              id="dq"
              type="search"
              placeholder="Hledat — ID, SPZ, jméno, telefon…"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
            />
            <button className="btn" type="submit" style={{ marginLeft: 8 }}>
              Hledat
            </button>
          </form>
        </div>
      </div>

      {/* This screen refreshes itself every minute, so a failure heals on its
          own — but "wait and see" is not what the person watching the board at
          06:00 wants to be told, and a silent red line is not an announcement. */}
      {isError && (
        <div className="alert" role="alert">
          Data se nepodařilo načíst
          {error instanceof Error && error.message ? ` (${error.message})` : ''}.{' '}
          <button className="btn sm" type="button" onClick={() => refetch()}>
            Zkusit znovu
          </button>
        </div>
      )}

      {forecast && s ? (
        /* A shift that has not started has no messages and no till. Instead
           of two cards printing zero, one card says what the board is: the
           plan for that shift, in the three numbers the desk staffs by. */
        <div className="card vyhled">
          <div className="h">
            <h2>Výhled</h2>
            <span className="phase forecast">plán podle rezervací</span>
          </div>
          <div className="stats">
            <span>
              <b>{CZK.format(s.sections.arrivals.total)}</b> příjezdů
            </span>
            <span>
              <b>{CZK.format(s.sections.departures.total)}</b> odjezdů
            </span>
            <span>
              <b>{CZK.format(s.sections.toTransfer.total)}</b> k převozu
            </span>
            <span>
              <b>{CZK.format(s.sections.lapsed.total)}</b> zrušených
            </span>
          </div>
          <div className="basis">
            Zaparkovalo, Odjelo a pokladna se týkají jen směny, která už běží — tady nejsou.
            Obsazenost a Služby ukazují stav teď, ne výhled.
          </div>
        </div>
      ) : (
      <>
      {/* The top of the screen, remade on 30 September ("taking too much
          space, remake the UI"): one slim strip carries the day figures on
          the left and the shift till on the right, in a row instead of a
          column, and the message board underneath takes only the height its
          messages need — an empty board is one line. */}
      {/* The top of the screen as a row of small KPI tiles (30 September:
          "all this information should be in small KPI cards horizontally,
          not long lines and cards"): the day figures, then the shift till,
          then the close button. One grid, so it wraps to two rows on a
          laptop instead of scrolling. */}
      <div className="kpirow" aria-label="Dnes a pokladna">
        <div className="kpi">
          <span className="l">Příjezdů dnes</span>
          <span className="v">{s ? CZK.format(s.arrivalsToday) : '…'}</span>
        </div>
        <div className="kpi">
          <span className="l">Odjezdů dnes</span>
          <span className="v">{s ? CZK.format(s.departuresToday) : '…'}</span>
        </div>
        {/* The figure and the panel that breaks it down are the same
            number, so the figure is the way into the panel. */}
        <button type="button" className="kpi" onClick={() => setTab('obsazenost')} title="Rozpad podle parkovišť a míst.">
          <span className="l">Na parkovišti</span>
          <span className="v">{s ? CZK.format(s.onSiteNow) : '…'}</span>
        </button>
        <div className="kpi" title="Vozy čekající na převoz Europort → Tuchoměřice / Tuchoměřice → Europort.">
          <span className="l">K převozu</span>
          <span className="v">{s ? `${s.pendingE2t} / ${s.pendingT2e}` : '…'}</span>
          <span className="d">tam / zpět</span>
        </div>
        <div className={s && s.unpaidCount > 0 ? 'kpi warn' : 'kpi'}>
          <span className="l">Nedoplatky</span>
          <span className="v">{s ? CZK.format(s.unpaidCount) : '…'}</span>
          <span className="d">{s && s.unpaidCount > 0 ? money(s.unpaidAmount) : '—'}</span>
        </div>
        <div className={s && s.missingCarCount > 0 ? 'kpi warn' : 'kpi'}>
          <span className="l">Chybí auto / SPZ</span>
          <span className="v">{s ? CZK.format(s.missingCarCount) : '…'}</span>
        </div>

      </div>

      {/* The till is ONE card ("the pokladna should be in one card"): its
          figures side by side, the total, and the close button, on a row of
          its own under the day tiles — a fixed place, whatever the width. */}
      {s?.tillHidden ? null : (
      <div className="kpirow tillrow2">
        <div className="kpi pokladna" title={till ? `Základ: ${CZK.format(till.cars)} vozů, které odjely během této směny.` : undefined}>
          <span className="l">Pokladna směny{till ? ` · ${CZK.format(till.cars)} vozů` : ''}</span>
          <div className="tf">
            <span className="tl">Hotovost</span>
            <span className="tv">{till ? money(till.cash) : '…'}</span>
          </div>
          <div className="tf">
            <span className="tl">Kartou</span>
            <span className="tv">{till ? money(till.card) : '…'}</span>
          </div>
          {/* The paystation's take (auto_cash / auto_card), shown only when it
              is not zero — the legacy till counts it beside cash and card. */}
          {till && till.onsiteOther !== 0 && (
            <div className="tf" title="Platby přes platební automat (auto_cash, auto_card).">
              <span className="tl">Automat</span>
              <span className="tv">{money(till.onsiteOther)}</span>
            </div>
          )}
          {/* GoPay and invoices only for the admins (30 Oct 2026: the desk's till is what it took in on site). */}
          {s?.tillOnsiteOnly ? null : (
            <>
              <div className="tf">
                <span className="tl">GoPay</span>
                <span className="tv">{till ? money(till.gopay) : '…'}</span>
              </div>
              <div className="tf">
                <span className="tl">Fakturace CK</span>
                <span className="tv">{till ? money(till.invoice) : '…'}</span>
              </div>
            </>
          )}
          {/* BitPay is no longer taken; it stays inside the total for the old
              rows that carry it and is not printed. */}
          <div className="tf total">
            <span className="tl">Celkem</span>
            <span className="tv">{till ? money(till.total) : '…'}</span>
          </div>
          <div className="closewrap">
            <CloseShiftForm />
          </div>
        </div>
      </div>
      )}

      {/* Interní zprávy moved to the navbar's envelope (MessagesBell) on 30
          September; the board no longer takes a row here. */}
      </>

      )}

      {/* The "Kamery na bránách" card stood here until 30 September ("get rid of
          cameras there"); the camera queue is on Převozy → Kamery. */}

      {/* The strip is the legacy one, in the legacy order, and it does what
          the legacy one does: switches the board below it between the shift
          and the car park, and — since 2 September — between this shift and
          the next ones. */}
      <div className="tabs" role="tablist" aria-label="Pohled na směnu">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'smena' && !forecast}
          className={tab === 'smena' && !forecast ? 'active' : undefined}
          onClick={() => {
            setTab('smena');
            setShiftOffset(0);
          }}
        >
          Aktuální směna
        </button>
        {/* The legacy drop-down, as a real one. Choosing an entry moves the
            whole board to that shift; "Aktuální směna" brings it back. A
            <select> rather than a menu of buttons because it IS a choice of
            one from a list, and the native control gets keyboard, screen
            reader and touch behaviour for free. */}
        <label className={forecast ? 'active shiftpick' : 'shiftpick'}>
          <span className="visually-hidden">Další směny</span>
          <select
            aria-label="Další směny"
            value={shiftOffset}
            onChange={(e) => {
              const n = Number(e.target.value);
              setShiftOffset(n);
              // A forecast is a view of the shift, so the band shows the
              // shift's chart — not the car park, which has no future tense.
              if (n > 0) setTab('smena');
            }}
          >
            <option value={0}>Další směny ▾</option>
            {shiftOptions.map((o) => (
              <option key={o.n} value={o.n}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'obsazenost'}
          className={tab === 'obsazenost' ? 'active' : undefined}
          onClick={() => setTab('obsazenost')}
          title="Kolik vozů kde stojí — Europort, Tuchoměřice, POP, Kněževes, Šestka."
        >
          Obsazenost
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'sluzby'}
          className={tab === 'sluzby' ? 'active' : undefined}
          onClick={() => setTab('sluzby')}
          title="Nevyřízené služby na vozech, které tu stojí — za všechna parkoviště."
        >
          Služby
          {/* The count sits on the tab so the queue is visible without
              opening it — the same reason the site strip sits above the band. */}
          {s?.services ? <span className="tab-count">{CZK.format(s.services.total)}</span> : null}
        </button>
      </div>

      {/* Above the band, not inside it, so it is on the screen whichever tab
          is selected. On Obsazenost it is a summary of the cards directly
          underneath — a repetition, and a cheap one: the alternative is a
          strip that appears and disappears as the tab changes, which is how a
          number gets read as having changed when only the view did. */}
      <SiteStrip
        data={s?.occupancy ?? []}
        caps={s?.capacities ?? NO_CAPS}
        isPending={isPending}
        onPick={(key) => {
          setTab('obsazenost');
          setFocusCard(key);
        }}
      />

      {tab === 'obsazenost' ? (
        <Occupancy
          data={s?.occupancy ?? []}
          caps={s?.capacities ?? NO_CAPS}
          isPending={isPending}
          openKey={openKey}
          setOpenKey={setOpenKey}
          focusCard={focusCard}
          clearFocus={() => setFocusCard(null)}
        />
      ) : tab === 'sluzby' ? (
        <>
      {/* Služby — the legacy dashboard's own tab, and now a tab here too.
          It was a full-width card at the foot of the page for a while, and on
          a normal day that card is a hundred rows: every outstanding job on
          every car that is here, ordered days ago. A hundred rows under the
          shift's own work made the page three times taller than the work.
          The legacy screen kept this list behind a tab, and that is where
          operators look for it, so that is where it lives. */}
      <SectionCard
        solo
        openKey={openKey}
        setOpenKey={setOpenKey}
        title="Služby"
        data={s?.services}
        isPending={isPending}
        cols={8}
        /* Služby is the one card the yard chip does not reach, and it says so
           rather than looking scoped. It is not a slice of this shift — it is
           every outstanding job on every car that is here, ordered days ago —
           and it comes from its own query over `reservation_extraservice`
           whose plan was measured rather than assumed. Saying "vše" out loud
           costs a line; letting a dispatcher believe POP has forty washes
           outstanding costs a morning. */
        empty="Žádné nevyřízené služby — ani na vozech, které tu stojí, ani na příjezdech do konce příští směny."
        // Two jobs on one car are two rows; the id alone would open both.
        rowKey={(r) => `${r.id}:${r.serviceRef}`}
        /* Not "zbytek v Rezervacích". There is no service filter on that
           grid, so the jobs past the cap cannot be found there — and an
           operator who goes looking and finds nothing concludes they do not
           exist. The card owns up to the cap instead. */
        overflow={(shown, total) =>
          `Zobrazeno prvních ${shown} z ${CZK.format(total)} — zbytek se sem nevejde.`
        }
        head={
          <tr>
            <th>Stav+ID</th>
            <th>Klient</th>
            <th>Kde</th>
            <th>Auto</th>
            <th>Služba</th>
            <th>Odjezd</th>
            <th className="num">Cena</th>
            <th></th>
          </tr>
        }
        row={(r, open) => (
          <>
            {serviceGroupHeads.has(`${r.id}:${r.serviceRef}`) && (
              <tr className="grouphead">
                <td colSpan={8}>
                  {serviceGroupHeads.get(`${r.id}:${r.serviceRef}`)!.label}
                  <span className="count">{serviceGroupHeads.get(`${r.id}:${r.serviceRef}`)!.count}</span>
                </td>
              </tr>
            )}
          <tr {...open(r)}>
            <td>
              <Stav r={r} />
            </td>
            <Klient r={r} />
            <Kde r={r} />
            <td>
              <Auto r={r} />
            </td>
            <td>{r.service}</td>
            {/* The departure is the deadline — the wash has to be done before
                the customer is at the desk — so it is what the list sorts by
                and it is on the row. */}
            <td className="time">
              {r.group === 'arrivals' ? <>příjezd {dayDt(r.arrival, today)}</> : dayDt(r.departure, today)}
            </td>
            <td className="num">{r.servicePrice > 0 ? money(r.servicePrice) : nic}</td>
            {/* The tick the legacy kept on the reservation form, here where
                the job is read off. Stops the row click itself. */}
            <td className="act">
              <ServiceDoneButton reservationId={r.id} serviceId={r.serviceRef} compact />
            </td>
          </tr>
          </>
        )}
      />
        </>
      ) : (
        <Chart buckets={s?.chart ?? []} />
      )}


      {/* Row 1, the beta's order: Příjezdy, then Europort — the cars that
          have pulled in at the airport and wait for a driver. Stacked: both
          carry the legacy's full row and need the width. See .pair.stack. */}
      <div className="pair stack">
        <SectionCard
          openKey={openKey}
          setOpenKey={setOpenKey}
          title="Příjezdy"
          expand="arrive"
          alert={(() => {
            const n = (s?.sections.arrivals?.rows ?? []).filter((r) => placeState(r)?.kind === 'missing').length;
            return n ? `${n} bez místa` : undefined;
          })()}
          note={placeSummary(s?.sections.arrivals?.rows ?? [])}
          data={s?.sections.arrivals}
          isPending={isPending}
          cols={11}
          empty='Na tuto směnu nezbývá žádný očekávaný příjezd.'
          /* The legacy Příjezdy row, column for column: Stav+ID · Klient ·
             Kde · Příjezd · Auto · SPZ · KeyCafe · Poznámka · Délka ·
             Zaplatit. It used to print Osob and a column called PIN here;
             PIN was the KeyCafe code under a name nobody at the desk uses,
             and Osob is not on the legacy row at all. */
          head={
            <tr>
              <th>Stav+ID</th>
              <th>Klient</th>
              <th>Kde</th>
              <th>Příjezd</th>
              <th>Auto</th>
              <th>SPZ</th>
              <th>KeyCafe</th>
              <th>Poznámka</th>
              <th className="num">Délka</th>
              <th className="num">Zaplatit</th>
              <th aria-label="Protokol">Prot.</th>
            </tr>
          }
          row={(r, open) => (
            <tr {...open(r)}>
              <td>
                <Stav r={r} />
              </td>
              <Klient r={r} />
              <Kde r={r} />
              {/* The legacy prints the date before a time that is not today's
                  ("30. 9." on a night shift's small hours); dayDt does the same. */}
              <td className="time">{dayDt(r.arrival, today)}</td>
              <td>
                {r.car ? (
                  <span className="carname" title={r.car}>
                    {r.car}
                  </span>
                ) : (
                  <span className="doplnit">Doplnit auto!</span>
                )}
              </td>
              <td>
                {r.spz ? (
                  <span className="spz">{r.spz}</span>
                ) : (
                  <span className="doplnit">Doplnit SPZ!</span>
                )}
              </td>
              <KeyCafe r={r} which="arrival" />
              <Poznamka r={r} />
              <td className="num">{dny(r.days)}</td>
              <td className="num">
                {r.outstanding > 0 ? (
                  <b>{money(r.outstanding)}</b>
                ) : (
                  <span className="paidchip">0 Kč</span>
                )}
              </td>
              <Prot r={r} />
            </tr>
          )}
        />

        <SectionCard
          openKey={openKey}
          setOpenKey={setOpenKey}
          title="Europort"
          /* The departure strip (Zaplatit, Platba, KeyCafe kód odjezd, Odjezd) open at once, as on
             Odjezdy — the manager, 30 October 2026: a customer who comes back before the car was
             moved leaves straight from Europort, and the desk should not hunt for the button. */
          expand="depart"
          data={s?.sections.europort}
          isPending={isPending}
          cols={10}
          /* The legacy dashboard's "Europort" tab, with its columns: cars
             that have arrived at the airport and are waiting to be driven to
             a yard. The other direction of K převozu, beside it on purpose:
             the two queues are one driver's day, out and back. Not scoped to
             the shift — a car waiting is waiting — but the yard chip reaches
             it like the others, and "Europort" is the chip that shows it. */
          empty="Na Europortu teď nečeká žádný vůz na odvoz na parkoviště."
          head={
            <tr>
              <th>Stav+ID</th>
              <th>Klient</th>
              <th>Kde</th>
              <th>Přijelo</th>
              <th>Odjezd</th>
              <th>Auto</th>
              <th>Stání</th>
              <th>KeyCafe</th>
              <th title="Je klíč teď ve schránce KeyCafe? Podle posledního otevření boxu.">Klíč</th>
              <th aria-label="Protokol">Prot.</th>
            </tr>
          }
          row={(r, open) => (
            <tr {...inProgress(open(r), r.transferE2tDriver, r.transferE2t)}>
              <td>
                <Stav r={r} />
                <Prevazi driver={r.transferE2tDriver} since={r.transferE2t} />
              </td>
              <Klient r={r} />
              <Kde r={r} />
              <td className="time">{r.arrivalReal ? dayDt(r.arrivalReal, today) : nic}</td>
              <td className="time">{dayDt(r.departure, today)}</td>
              <td>
                <Auto r={r} />
              </td>
              <Misto r={r} />
              <KeyCafe r={r} which="arrival" />
              <KlicBox r={r} today={today} />
              <Prot r={r} />
            </tr>
          )}
        />
      </div>

      {/* Row 2, the beta's order: K převozu (the Z Tuchoměřic / Z POPu /
          Ze Šestky / Z Kněževse tabs — what must leave each yard for the
          airport) and then Odjezdy, the cars that are back at Europort and
          due to go. Stacked: both carry the legacy's full row. */}
      <div className="pair stack">
        <SectionCard
          openKey={openKey}
          setOpenKey={setOpenKey}
          title="K převozu"
          /* The departure strip open at once here too (the manager, 30 October 2026: "add it to
             Z POPu, Z Tucho, Z Kněževsi a ostatní") — a customer collected straight from a yard pays
             and leaves from this row. */
          expand="depart"
          badge="výhled"
          data={yardData}
          tabs={yardTabs}
          activeTab={yard}
          onTab={setTransferYard}
          isPending={isPending}
          cols={8}
          empty={
            yard === ALL_YARDS
              ? 'Žádný vůz nečeká na převoz zpět na Europort.'
              : 'Z tohoto parkoviště teď nic nečeká na převoz.'
          }
          head={
            <tr>
              <th className="num">ID</th>
              <th aria-label="Druh úkonu" />
              <th>Klient</th>
              <th>Auto</th>
              <th>Stání</th>
              <th>Kam</th>
              <th>Odjezd</th>
              <th className="num">Zbývá</th>
            </tr>
          }
          row={(r, open) => {
            const h = hoursUntil(r.departure, clock);
            return (
              <tr {...inProgress(open(r), r.transferT2eDriver, r.transferT2e)}>
                <td className="num">
                  {r.id}
                  <Prevazi driver={r.transferT2eDriver} since={r.transferT2e} />
                </td>
                <td aria-hidden="true">🚘</td>
                <Klient r={r} />
                <td>
                  <Auto r={r} />
                </td>
                {/* Místo and Kam, as on every legacy transfer tab: where the
                    car stands and where it is going. A driver reads these two
                    and leaves; before, they were a click away in the drawer. */}
                <Misto r={r} />
                <td className="misto">{r.parkingTarget ? spotLabel(r.parkingTarget) : nic}</td>
                <td className="time">{dayDt(r.departure, today)}</td>
                <td className="num">
                  {h == null ? (
                    '—'
                  ) : h < 0 ? (
                    // Already due. This is the row somebody has to act on
                    // now, so it does not get to look like all the others.
                    <span className="badge-pen">{`po termínu`}</span>
                  ) : (
                    `${h} h`
                  )}
                </td>
              </tr>
            );
          }}
        />
        <SectionCard
          openKey={openKey}
          setOpenKey={setOpenKey}
          title="Odjezdy"
          expand="depart"
          data={s?.sections.departures}
          isPending={isPending}
          cols={10}
          empty="Na tuto směnu nezbývá žádný odjezd."
          /* Legacy: Stav+ID · Klient · Kde · Odjezd · KeyCafe · Auto · SPZ ·
             Místo · Zaplatit. Auto and SPZ share a cell here as they do on
             every other card; KeyCafe is the departure code — the box the
             customer collects from — and Místo is where the car stands, in
             words, so a valet sent for it does not have to open the row. */
          head={
            <tr>
              <th>Stav+ID</th>
              <th>Klient</th>
              <th>Kde</th>
              <th>Odjezd</th>
              <th>KeyCafe</th>
              <th title="Je klíč teď ve schránce KeyCafe? Podle posledního otevření boxu.">Klíč</th>
              <th>Auto</th>
              <th>Stání</th>
              <th className="num">Zaplatit</th>
              <th aria-label="Protokol">Prot.</th>
            </tr>
          }
          row={(r, open) => (
            <tr {...open(r)}>
              <td>
                <Stav r={r} />
              </td>
              <Klient r={r} />
              <Kde r={r} />
              <td className="time">
                {dayDt(r.departure, today)}
                <DepartureFlightNote f={r.departureFlight} />
              </td>
              <KeyCafe r={r} which="departure" />
              {/* The dispatchers' calls of 30 and 31 Oct 2026: is the key in the box now, which box, since when. */}
              <KlicBox r={r} today={today} />
              <td>
                <Auto r={r} />
              </td>
              <Misto r={r} />
              <td className="num">
                {r.outstanding > 0 ? (
                  <b>{money(r.outstanding)}</b>
                ) : (
                  <span className="paidchip">0 Kč</span>
                )}
              </td>
              <Prot r={r} />
            </tr>
          )}
        />
      </div>

      {/* pair 3: Zaparkovalo | Odjelo — what happened, so only for a shift
          that is happening. On a forecast both would print zero under a
          heading in the past tense, and a zero there reads as "nobody came",
          which is a claim about a shift that has not started. */}
      {!forecast && (
      <div className="pair">
        <SectionCard
          openKey={openKey}
          setOpenKey={setOpenKey}
          title="Zaparkovalo"
          data={s?.sections.parked}
          isPending={isPending}
          cols={5}
          empty="Během této směny zatím nikdo nezaparkoval."
          head={
            <tr>
              <th className="num">ID</th>
              <th>Klient</th>
              <th>Kde</th>
              <th>Přijelo</th>
              <th>Auto</th>
            </tr>
          }
          row={(r, open) => (
            <tr {...open(r)}>
              <td className="num">{r.id}</td>
              <Klient r={r} />
              <Kde r={r} />
              <td className="time">{dayDt(r.arrivalReal, today)}</td>
              <td>
                <Auto r={r} />
              </td>
            </tr>
          )}
        />

        <SectionCard
          openKey={openKey}
          setOpenKey={setOpenKey}
          title="Odjelo"
          data={s?.sections.left}
          isPending={isPending}
          cols={9}
          empty="Během této směny zatím nikdo neodjel."
          head={
            <tr>
              <th className="num">ID</th>
              <th>Klient</th>
              <th>Kde</th>
              <th>Odjelo</th>
              <th>Auto</th>
              <th className="num">Zaplaceno</th>
              <th className="num">GoPay</th>
              <th className="num">Fakturace CK</th>
              <th>Platba</th>
            </tr>
          }
          row={(r, open) => (
            <tr {...open(r)}>
              <td className="num">
                <span aria-hidden="true">{statusIcon(r)} </span>
                {r.id}
              </td>
              <Klient r={r} />
              <Kde r={r} />
              <td className="time">{dayDt(r.departureReal, today)}</td>
              <td>
                <Auto r={r} />
              </td>
              {/* The legacy Odjelo row: what was collected, by channel. On site
                  (cash or card, as Platba says), GoPay, invoiced to the agency.
                  BitPay is folded into the on-site figure on the rare row that
                  has it. The badge is what is still owed, as before. */}
              <td className="num">
                {r.paidOnSite + r.paidBitpay > 0 ? money(r.paidOnSite + r.paidBitpay) : nic}{' '}
                {r.outstanding > 0 && <span className="badge-pen">{money(r.outstanding)}</span>}
              </td>
              <td className="num">{r.paidGopay > 0 ? money(r.paidGopay) : nic}</td>
              <td className="num">{r.paidInvoice > 0 ? money(r.paidInvoice) : nic}</td>
              <td>{r.payment ? (PAYMENT_LABEL[r.payment] ?? r.payment) : nic}</td>
            </tr>
          )}
        />
      </div>

      )}

      {/* Propadlé a zrušené — on its own row now that K převozu sits beside
          Europort. Full width is more than it needs; `solo` keeps the card's
          own rhythm rather than leaving half a pair empty. */}
        <SectionCard
          openKey={openKey}
          setOpenKey={setOpenKey}
          solo
          title="Propadlé a zrušené"
          data={s?.sections.lapsed}
          isPending={isPending}
          cols={6}
          empty='Žádné propadlé ani zrušené rezervace v této směně.'
          head={
            <tr>
              <th>Stav+ID</th>
              <th>Klient</th>
              <th>Kde</th>
              <th>Mělo přijet</th>
              <th>Auto</th>
              <th className="num">Osob</th>
            </tr>
          }
          row={(r, open) => (
            <tr {...open(r)}>
              <td>
                <Stav r={r} />
              </td>
              <Klient r={r} />
              <Kde r={r} />
              <td className="time">{dayDt(r.arrival, today)}</td>
              <td>
                <Auto r={r} />
              </td>
              <td className="num">{r.person ?? 0}</td>
            </tr>
          )}
        />


      <div className="card legenda">
        <h3>Legenda</h3>
        <div className="items">
          <span>🔥 Nová</span>
          <span>◎ Předplacená</span>
          <span>🅿 Aktivní (parkuje)</span>
          <span>✔ Uzavřená</span>
          <span>❗ Propadlá</span>
          <span>⊘ Zrušená</span>
          <span>✈ Letí</span>
          <span>🚩 Partner</span>
          <span>🏠 / 🅐 Garáž / Venku</span>
          <span>
            <i className="sw" style={{ background: 'var(--row-recent)' }} />
            čerstvá změna
          </span>
          <span>
            <i className="sw" style={{ background: 'var(--row-paid)' }} />
            zaplaceno
          </span>
          {/* The mockup's third swatch — "řidič převáží" — is not here. It
              needs the live dispatch state from the driver app, which lands in
              Phase 1. A colour in the legend that never appears on a row
              teaches people to distrust the legend. */}
        </div>
      </div>

      <Footer />
    </div>
  );
}
